class ListicleVideo extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.src || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    if (document.readyState === "complete") {
      this.observe();
    } else {
      window.addEventListener("load", () => this.observe(), { once: true });
    }
  }

  disconnectedCallback() {
    this.loadObserver?.disconnect();
    this.playObserver?.disconnect();
  }

  observe() {
    this.loadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.loadObserver.disconnect();
        this.createVideo();
      },
      { rootMargin: "400px 0px" }
    );
    this.loadObserver.observe(this);

    this.playObserver = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting;
        if (!this.video) return;
        if (this.isVisible) {
          this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      },
      { threshold: 0.2 }
    );
    this.playObserver.observe(this);
  }

  createVideo() {
    const video = document.createElement("video");
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("aria-hidden", "true");
    video.preload = "auto";
    video.src = this.dataset.src;
    video.addEventListener("playing", () => video.classList.add("is-playing"), { once: true });
    this.append(video);
    this.video = video;
    if (this.isVisible) video.play().catch(() => {});
  }
}

class ListicleTapVideo extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this);
  }

  handleEvent(event) {
    switch (event.target.dataset.action) {
      case "play-video":
        this.play(event.target);
        break;
    }
  }

  play(button) {
    const video = document.createElement("video");
    video.muted = true;
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.src = this.dataset.src;
    video.classList.add("is-playing");
    button.replaceWith(video);
    video.play().catch(() => {});
  }
}

class ListicleCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-track]");
    this.prev = this.querySelector('[data-action="prev"]');
    this.next = this.querySelector('[data-action="next"]');
    this.addEventListener("click", this);
    this.onScroll = () => this.updateArrows();
    this.track.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll, { passive: true });
    this.updateArrows();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onScroll);
  }

  handleEvent(event) {
    switch (event.target.dataset.action) {
      case "prev":
        this.scrollByCard(-1);
        break;
      case "next":
        this.scrollByCard(1);
        break;
    }
  }

  scrollByCard(direction) {
    const card = this.track.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: "smooth" });
  }

  updateArrows() {
    if (!this.prev || !this.next) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    this.prev.disabled = this.track.scrollLeft <= 1;
    this.next.disabled = this.track.scrollLeft >= maxScroll - 1;
  }
}

class ListicleOffer extends HTMLElement {
  connectedCallback() {
    this.mainImage = this.querySelector("[data-main-image] img");
    this.addEventListener("click", this);
  }

  handleEvent(event) {
    switch (event.target.dataset.action) {
      case "select-colour":
        this.selectColour(event.target);
        break;
    }
  }

  selectColour(button) {
    this.querySelectorAll('[data-action="select-colour"]').forEach((item) => {
      const isActive = item === button;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-pressed", isActive);
    });

    if (this.mainImage && button.dataset.imageSrc) {
      this.mainImage.removeAttribute("srcset");
      this.mainImage.src = button.dataset.imageSrc;
      if (button.dataset.imageSrcset) this.mainImage.srcset = button.dataset.imageSrcset;
      this.mainImage.alt = button.dataset.imageAlt || "";
    }

    if (button.dataset.url) {
      document.querySelectorAll("[data-listicle-product-link]").forEach((link) => {
        link.href = button.dataset.url;
      });
    }
  }
}

class ListicleSticky extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector("[data-listicle-sticky-trigger]");
    this.offer = document.getElementById("offer");
    if (!this.trigger) return;

    this.onChange = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };
    window.addEventListener("scroll", this.onChange, { passive: true });
    window.addEventListener("resize", this.onChange, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onChange);
    window.removeEventListener("resize", this.onChange);
  }

  // Scroll position, not IntersectionObserver: an anchor jump can skip past the trigger without crossing the viewport.
  update() {
    const isPastTrigger = this.trigger.getBoundingClientRect().bottom < 0;
    const offerRect = this.offer?.getBoundingClientRect();
    const isOfferInView = offerRect ? offerRect.top < window.innerHeight && offerRect.bottom > 0 : false;
    const isVisible = isPastTrigger && !isOfferInView;
    this.classList.toggle("is-visible", isVisible);
    this.inert = !isVisible;
  }
}

if (document.querySelector("listicle-video")) customElements.define("listicle-video", ListicleVideo);
if (document.querySelector("listicle-tap-video")) customElements.define("listicle-tap-video", ListicleTapVideo);
if (document.querySelector("listicle-carousel")) customElements.define("listicle-carousel", ListicleCarousel);
if (document.querySelector("listicle-offer")) customElements.define("listicle-offer", ListicleOffer);
if (document.querySelector("listicle-sticky")) customElements.define("listicle-sticky", ListicleSticky);
